import { z } from "zod";
import type { SuggestedAction } from "../warning/suggested-action.js";
import type { WarningTarget } from "../warning/target.js";
import type { Warning, WarningKind, WarningStatus } from "../warning/warning.js";
import { CodecError, instantSchema } from "./envelope.js";

/**
 * Phase 6a codec for `Warning` and its sub-types: `WarningTarget`,
 * `SuggestedAction`. Determinism: declared key order; undefined
 * optionals omitted; arrays preserve scan order at the call site
 * (`scanWarnings` already returns deterministic order in Phase 3).
 *
 * Decode reconstructs the value directly rather than going through the
 * Phase 3 state machine — the state machine produces *new* warnings
 * from candidates, while the codec restores the persisted exact state
 * (status, dismissalNote, timestamps, signature all intact, including
 * `auto-resolved` records). The repository boundary in Phase 6d
 * enforces auto-resolved immutability on write; the codec passes the
 * stored value through verbatim.
 */

const warningKindSchema: z.ZodType<WarningKind> = z.enum([
  "transient-description",
  "amp-wire-mismatch",
  "non-standard-amperage",
]);

const warningStatusSchema: z.ZodType<WarningStatus> = z.enum([
  "open",
  "dismissed",
  "auto-resolved",
]);

const positionJsonSchema = z.object({
  row: z.number().int().nonnegative(),
  column: z.number().int().nonnegative(),
});

export const warningTargetJsonSchema: z.ZodType<WarningTarget> =
  z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("circuit"), circuitId: z.string().min(1) }),
    z.object({
      kind: z.literal("breaker"),
      panelId: z.string().min(1),
      origin: positionJsonSchema,
    }),
    z.object({ kind: z.literal("panel"), panelId: z.string().min(1) }),
    z.object({ kind: z.literal("device"), deviceId: z.string().min(1) }),
  ]);

export function encodeWarningTarget(t: WarningTarget): Record<string, unknown> {
  switch (t.kind) {
    case "circuit":
      return { kind: "circuit", circuitId: t.circuitId };
    case "breaker":
      return {
        kind: "breaker",
        panelId: t.panelId,
        origin: { row: t.origin.row, column: t.origin.column },
      };
    case "panel":
      return { kind: "panel", panelId: t.panelId };
    case "device":
      return { kind: "device", deviceId: t.deviceId };
  }
}

export function decodeWarningTarget(input: unknown): WarningTarget {
  const parsed = warningTargetJsonSchema.safeParse(input);
  if (!parsed.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodeWarningTarget: ${parsed.error.message}`,
    );
  }
  return parsed.data;
}

const wireMaterialSchema = z.enum(["copper", "aluminum", "copper-clad-aluminum"]);

export const suggestedActionJsonSchema: z.ZodType<SuggestedAction> =
  z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("rewrite-description"),
      newText: z.string(),
    }),
    z.object({
      kind: z.literal("change-wire-gauge"),
      newGauge: z.string(),
      material: wireMaterialSchema,
    }),
    z.object({
      kind: z.literal("change-amperage"),
      newAmperage: z.number().int().positive(),
    }),
  ]);

export function encodeSuggestedAction(
  a: SuggestedAction,
): Record<string, unknown> {
  switch (a.kind) {
    case "rewrite-description":
      return { kind: "rewrite-description", newText: a.newText };
    case "change-wire-gauge":
      return {
        kind: "change-wire-gauge",
        newGauge: a.newGauge,
        material: a.material,
      };
    case "change-amperage":
      return { kind: "change-amperage", newAmperage: a.newAmperage };
  }
}

export function decodeSuggestedAction(input: unknown): SuggestedAction {
  const parsed = suggestedActionJsonSchema.safeParse(input);
  if (!parsed.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodeSuggestedAction: ${parsed.error.message}`,
    );
  }
  return parsed.data;
}

export const warningJsonSchema = z.object({
  id: z.string().min(1),
  kind: warningKindSchema,
  target: warningTargetJsonSchema,
  signature: z.string(),
  citationId: z.string().min(1).optional(),
  message: z.string(),
  suggestedAction: suggestedActionJsonSchema.optional(),
  status: warningStatusSchema,
  dismissalNote: z.string().optional(),
  firstSeenAt: instantSchema,
  lastStatusChangeAt: instantSchema,
});

export type WarningJson = z.infer<typeof warningJsonSchema>;

export function encodeWarning(w: Warning): Record<string, unknown> {
  const out: Record<string, unknown> = {
    id: w.id,
    kind: w.kind,
    target: encodeWarningTarget(w.target),
    signature: w.signature,
  };
  if (w.citationId !== undefined) out["citationId"] = w.citationId;
  out["message"] = w.message;
  if (w.suggestedAction !== undefined) {
    out["suggestedAction"] = encodeSuggestedAction(w.suggestedAction);
  }
  out["status"] = w.status;
  if (w.dismissalNote !== undefined) out["dismissalNote"] = w.dismissalNote;
  out["firstSeenAt"] = w.firstSeenAt;
  out["lastStatusChangeAt"] = w.lastStatusChangeAt;
  return out;
}

export function decodeWarning(input: unknown): Warning {
  const parsed = warningJsonSchema.safeParse(input);
  if (!parsed.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodeWarning: ${parsed.error.message}`,
    );
  }
  const j = parsed.data;
  return {
    id: j.id,
    kind: j.kind,
    target: j.target,
    signature: j.signature,
    citationId: j.citationId,
    message: j.message,
    suggestedAction: j.suggestedAction,
    status: j.status,
    dismissalNote: j.dismissalNote,
    firstSeenAt: j.firstSeenAt,
    lastStatusChangeAt: j.lastStatusChangeAt,
  };
}
